// =============================================================================
// SPARRING SERVICE - Firebase CRUD for SparringSessions
// Fase 2: Sparring = denken, betekenis, herkenningspunten
// =============================================================================

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  query,
  where,
  getDocs,
  orderBy,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { SparringSession, SparringMessage, SparringMode } from '../types';

const COLLECTION_NAME = 'sparringSessions';

// -----------------------------------------------------------------------------
// CREATE
// -----------------------------------------------------------------------------

/**
 * Create a new sparring session for a goal
 * @param ownerId - Firebase Auth UID
 * @param goalId - The goal this session belongs to
 * @param mode - 'structured' or 'free'
 * @returns The created session's ID
 */
export async function createSparringSession(
  ownerId: string,
  goalId: string,
  mode: SparringMode
): Promise<string> {
  const sessionData: Omit<SparringSession, 'id'> = {
    ownerId,
    goalId,
    mode,
    messages: [],
    createdAt: Date.now(),
    milestonesCreated: [],
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), sessionData);
  
  // Update the document with its own ID for consistency
  await updateDoc(docRef, { id: docRef.id });
  
  return docRef.id;
}

// -----------------------------------------------------------------------------
// READ
// -----------------------------------------------------------------------------

/**
 * Get a sparring session by ID
 * @param sessionId - The session's document ID
 * @returns The SparringSession or null if not found
 */
export async function getSessionById(sessionId: string): Promise<SparringSession | null> {
  const docRef = doc(db, COLLECTION_NAME, sessionId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as SparringSession;
}

/**
 * Get all sparring sessions for a specific goal
 * @param ownerId - Firebase Auth UID
 * @param goalId - The goal's ID
 * @returns Array of SparringSession objects
 */
export async function getSessionsByGoal(
  ownerId: string,
  goalId: string
): Promise<SparringSession[]> {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId)
  );

  const snapshot = await getDocs(q);
  
  // Sort in memory
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as SparringSession))
    .sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Get all sparring sessions for a user
 * @param ownerId - Firebase Auth UID
 * @returns Array of SparringSession objects
 */
export async function getAllSessions(ownerId: string): Promise<SparringSession[]> {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId)
  );

  const snapshot = await getDocs(q);
  
  // Sort in memory
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as SparringSession))
    .sort((a, b) => b.createdAt - a.createdAt);
}

// -----------------------------------------------------------------------------
// UPDATE
// -----------------------------------------------------------------------------

/**
 * Add a message to an existing sparring session
 * @param sessionId - The session's document ID
 * @param message - The message to add (without id, timestamp will be set)
 */
export async function addMessageToSession(
  sessionId: string,
  message: Omit<SparringMessage, 'id' | 'timestamp'>
): Promise<void> {
  // ✅ FIX: Reject empty messages to prevent step progression bugs
  if (!message.content || message.content.trim().length === 0) {
    console.warn('⚠️ [sparring.service] Attempted to add empty message - rejected');
    throw new Error('Cannot add empty message to sparring session');
  }

  const fullMessage: SparringMessage = {
    ...message,
    id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: Date.now(),
  };

  const docRef = doc(db, COLLECTION_NAME, sessionId);
  await updateDoc(docRef, {
    messages: arrayUnion(fullMessage),
  });
}

/**
 * Close a sparring session
 * @param sessionId - The session's document ID
 */
export async function closeSession(sessionId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, sessionId);
  await updateDoc(docRef, {
    closedAt: Date.now(),
  });
}

/**
 * Add a milestone ID to the session's milestonesCreated array
 * @param sessionId - The session's document ID
 * @param milestoneId - The milestone's ID to add
 */
export async function addMilestoneToSession(
  sessionId: string,
  milestoneId: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, sessionId);
  await updateDoc(docRef, {
    milestonesCreated: arrayUnion(milestoneId),
  });
}

/**
 * Update a specific message in a sparring session
 * @param sessionId - The session's document ID
 * @param messageId - The message's ID to update
 * @param newContent - The new content for the message
 */
export async function updateMessageInSession(
  sessionId: string,
  messageId: string,
  newContent: string
): Promise<void> {
  // Get current session
  const session = await getSessionById(sessionId);
  if (!session) {
    throw new Error('Session not found');
  }

  // Find and update the message
  const updatedMessages = session.messages.map(msg => {
    if (msg.id === messageId) {
      return {
        ...msg,
        content: newContent,
        editedAt: Date.now(),
      };
    }
    return msg;
  });

  // Write back the updated messages array
  const docRef = doc(db, COLLECTION_NAME, sessionId);
  await updateDoc(docRef, {
    messages: updatedMessages,
  });
}
